import React, { useEffect, useState } from 'react';
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import { useWorkflowStore } from "../../store/useWorkflowStore";
import { TargetFacilitySelector } from "./selectors/TargetFacilitySelector";
import { TargetUseCaseSelector } from "./selectors/TargetUseCaseSelector";
import { ImportTypeSelector } from "./selectors/ImportTypeSelector";
import { TargetAuthorSelector } from "./selectors/TargetAuthorSelector";
import { RevisionConfirmDialog } from "./RevisionConfirmDialog";
import { useToast } from "../../lib/use-toast";

/**
 * TargetInstanceForm component
 *
 * Simplified form for selecting target details using SERVER_CONFIG endpoint
 * No need to select instance/client/database - uses configured DWI instance directly
 */
interface TargetInstanceFormProps {
  onNext: () => void;
  /** When 'ontology', the form requires only target instance (no facility / use case / author).
   *  Ontology objects are facility-agnostic so we don't gate the user on those. */
  workflowType?: 'process' | 'ontology';
}

const TargetInstanceForm: React.FC<TargetInstanceFormProps> = ({ onNext, workflowType = 'process' }) => {
  const {
    isFormValid,
    setIsFormValid,
    targetServiceId,
    targetFacilityId,
    targetUseCaseId,
    targetImportType,
    targetChecklistId,
    targetChecklistName,
    targetAuthorId,
    includeObjects,
    selectedChecklists,
    setTargetFacilityId,
    setTargetFacilityName,
    setTargetUseCaseId,
    setTargetAuthorId,
  } = useWorkflowStore();

  // Revision overwrites the picked target process — require an explicit source⇄target
  // name confirmation before advancing (RevisionConfirmDialog).
  const [revisionConfirmOpen, setRevisionConfirmOpen] = useState(false);

  const isOntology = workflowType === 'ontology';
  // Ontology metadata is facility-agnostic, but EntityObject instances are facility-scoped — so a
  // target facility is required for ontology only when "include objects" is on.
  const ontologyNeedsFacility = isOntology && includeObjects;

  const { toast } = useToast();

  // When target instance changes, reset everything downstream so the user re-picks against
  // the new instance's facilities / use cases / users.
  useEffect(() => {
    setTargetFacilityId(null);
    setTargetFacilityName(null);
    setTargetUseCaseId(null);
    setTargetAuthorId(null);
  }, [targetServiceId, setTargetFacilityId, setTargetFacilityName, setTargetUseCaseId, setTargetAuthorId]);

  // Update form validity. Ontology = only target instance. Process = full target context.
  useEffect(() => {
    if (isOntology) {
      // Require a real target facility when objects are being migrated; metadata-only just needs the instance.
      setIsFormValid(!!targetServiceId && (!ontologyNeedsFacility || (!!targetFacilityId && targetFacilityId !== "-1")));
      return;
    }
    let isValid =
      !!targetServiceId &&
      !!targetFacilityId &&
      !!targetUseCaseId &&
      !!targetImportType &&
      !!targetAuthorId;

    // If revision is selected, also require targetChecklistId
    if (targetImportType === 'revision') {
      isValid = isValid && !!targetChecklistId;
    }

    setIsFormValid(isValid);
  }, [
    isOntology,
    ontologyNeedsFacility,
    targetServiceId,
    targetFacilityId,
    targetUseCaseId,
    targetImportType,
    targetChecklistId,
    targetAuthorId,
    setIsFormValid
  ]);

  // Handle form submission
  const handleNext = () => {
    if (!isFormValid) {
      let errorMessage = "Please complete all required fields";
      if (isOntology) {
        if (!targetServiceId) errorMessage = "Please select target instance";
        else if (ontologyNeedsFacility && (!targetFacilityId || targetFacilityId === "-1")) errorMessage = "Please select a target facility";
      } else {
        if (!targetServiceId) errorMessage = "Please select target instance";
        else if (!targetFacilityId) errorMessage = "Please select target facility";
        else if (!targetUseCaseId) errorMessage = "Please select target use case";
        else if (!targetImportType) errorMessage = "Please select import type";
        else if (targetImportType === 'revision' && !targetChecklistId) errorMessage = "Please select checklist to revise";
        else if (!targetAuthorId) errorMessage = "Please select target author";
      }

      toast({
        variant: "destructive",
        title: "Validation Error",
        description: errorMessage,
      });
      return;
    }

    // A revision overwrites the selected target process — confirm the pairing first.
    if (!isOntology && targetImportType === 'revision') {
      setRevisionConfirmOpen(true);
      return;
    }

    // All validations passed, proceed to next step
    onNext();
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Target</h2>
      <p className="text-muted-foreground mb-6">
        {ontologyNeedsFacility
          ? "Object instances are facility-scoped — pick the target facility to create them under."
          : isOntology
            ? "Pick the target instance to push ontology records to. Ontology metadata is facility-agnostic, so no facility/use-case is required here."
            : "Pick the target instance, then its facility / use case / import details."}
      </p>

      <div className="flex flex-col space-y-4">
        {/* Target instance is chosen in the shared source⇄target bar above; this step only
            collects the target facility / use case / import details. Ontology metadata needs none;
            ontology WITH objects needs just the facility (objects are facility-scoped). */}
        {!isOntology && (
          <>
            <TargetFacilitySelector className="w-full" />
            <TargetUseCaseSelector className="w-full" />
            <ImportTypeSelector className="w-full" />
            <TargetAuthorSelector className="w-full" />
          </>
        )}
        {ontologyNeedsFacility && <TargetFacilitySelector className="w-full" />}
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleNext}
          disabled={false}
          variant={isFormValid ? "default" : "outline"}
          className="px-6"
        >
          Next
        </Button>
      </div>

      <RevisionConfirmDialog
        open={revisionConfirmOpen}
        onOpenChange={setRevisionConfirmOpen}
        sourceName={selectedChecklists[0]?.name ?? ""}
        sourceCode={selectedChecklists[0]?.code}
        targetName={targetChecklistName ?? targetChecklistId ?? ""}
        onConfirm={() => {
          setRevisionConfirmOpen(false);
          onNext();
        }}
      />
    </div>
  );
};

export default TargetInstanceForm;
